import { useEffect, useState } from "react";
import type { AxiosResponse } from "axios";
import moment from "moment";
import { toast } from "react-toastify";

import { downloadFile } from "src/utils/download.utils";
import { getReportRangeErrorMessage } from "src/utils/report/reportRange.util";

interface UseReportPdfDownloadOptions<TFilters> {
  /** Filters of the report currently on screen; the PDF is generated for exactly these. */
  filters: TFilters;
  /** Service call returning the PDF blob for the given filters. */
  download: (filters: TFilters) => Promise<AxiosResponse<Blob>>;
  /** File name prefix, e.g. `account-report-summary` → `account-report-summary-20260928-101500.pdf`. */
  fileNamePrefix: string;
}

/**
 * PDF export state for a report table: tracks generation, and marks the export as done until
 * the filters change (so MTable can show the "downloaded" state for the current filters only).
 */
export const useReportPdfDownload = <TFilters>({ filters, download, fileNamePrefix }: UseReportPdfDownloadOptions<TFilters>) => {
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [prevFilters, setPrevFilters] = useState<TFilters | null>(null);

  useEffect(() => {
    if (pdfSuccess && JSON.stringify(filters) !== JSON.stringify(prevFilters)) {
      setPdfSuccess(false);
    }
  }, [filters, prevFilters, pdfSuccess]);

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    setPdfSuccess(false);
    try {
      const response = await download(filters);
      downloadFile(response.data, `${fileNamePrefix}-${moment().format("YYYYMMDD-HHmmss")}.pdf`);
      toast.success("PDF downloaded successfully");

      setPdfSuccess(true);
      setPrevFilters(filters);
    } catch (error) {
      console.error("PDF download failed:", error);
      toast.error((await getReportRangeErrorMessage(error)) ?? "Failed to download PDF");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  return { isPdfGenerating, pdfSuccess, prevFilters, handleDownloadPdfClick };
};
